import { useEffect } from 'react'

export function useMobileCardHeight(track, language, ratio, copySelector, artworkSelector) {
  useEffect(() => {
    const root = track.current
    if (!root?.children.length) return
    const mobile = window.matchMedia('(max-width: 600px)')
    const cards = [...root.children]
    let frame = 0, disposed = false, width = root.clientWidth
    const clear = () => {
      delete root.dataset.mobileHeightCompact
      root.style.removeProperty('--mobile-card-height')
    }
    const measure = () => {
      frame = 0
      if (disposed) return
      clear()
      if (!mobile.matches || !root.clientWidth) return
      // Restore natural card layout before measuring so repeated resizes never compound the ratio.
      const baseline = Math.max(...cards.map(card => card.offsetHeight))
      root.dataset.mobileHeightCompact = 'true'
      const minimum = Math.max(...cards.map(card => {
        const style = getComputedStyle(card)
        const artStyle = getComputedStyle(card.querySelector(artworkSelector))
        const chrome = ['paddingTop', 'paddingBottom', 'borderTopWidth', 'borderBottomWidth']
          .reduce((sum, property) => sum + parseFloat(style[property]), 0)
        const copyHeight = [...card.querySelectorAll(copySelector)].reduce((sum, element) => sum + element.offsetHeight, 0)
        const artSpace = ['minHeight', 'paddingTop', 'paddingBottom', 'marginTop', 'marginBottom']
          .reduce((sum, property) => sum + (parseFloat(artStyle[property]) || 0), 0)
        return chrome + copyHeight + artSpace
      }))
      root.style.setProperty('--mobile-card-height', `${Math.max(baseline * ratio, minimum)}px`)
    }
    const schedule = () => {
      if (!disposed && !frame) frame = requestAnimationFrame(measure)
    }
    const sizes = new WeakMap()
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.target === root) {
          if (root.clientWidth !== width) { width = root.clientWidth; schedule() }
        } else {
          const size = `${entry.contentRect.width}:${entry.contentRect.height}`
          if (sizes.get(entry.target) !== size) { sizes.set(entry.target, size); schedule() }
        }
      }
    })
    measure()
    observer.observe(root)
    root.querySelectorAll(copySelector).forEach(element => observer.observe(element))
    mobile.addEventListener('change', schedule)
    document.fonts?.addEventListener('loadingdone', schedule)
    document.fonts?.ready.then(schedule)
    return () => {
      disposed = true
      cancelAnimationFrame(frame)
      observer.disconnect()
      mobile.removeEventListener('change', schedule)
      document.fonts?.removeEventListener('loadingdone', schedule)
      clear()
    }
  }, [track, language, ratio, copySelector, artworkSelector])
}
